import { useCallback, useEffect, useState } from "react";

import {
  deleteEmployeeCertification,
  fetchCertificationMaster,
  fetchMyEmployeeCertifications,
  updateEmployeeCertifications,
} from "../services/employee-certifications.service.ts";
import type {
  CertificationCategory,
  EmployeeCertificationResponse,
  UpdateEmployeeCertificationsRequest,
} from "../types.ts";

export function useEmployeeCertifications(accessToken?: string) {
  const [data, setData] = useState<EmployeeCertificationResponse | null>(null);
  const [categories, setCategories] = useState<CertificationCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError("");
      try {
        const [response, master] = await Promise.all([
          fetchMyEmployeeCertifications(accessToken, controller.signal),
          fetchCertificationMaster(accessToken, controller.signal),
        ]);
        if (!controller.signal.aborted) {
          setData(response);
          setCategories(master);
        }
      } catch {
        if (!controller.signal.aborted) {
          setError("Unable to load certifications.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [accessToken]);

  const save = useCallback(
    async (request: UpdateEmployeeCertificationsRequest) => {
      setSaving(true);
      try {
        await updateEmployeeCertifications(request, accessToken);
        const refreshed = await fetchMyEmployeeCertifications(accessToken);
        setData(refreshed);
      } finally {
        setSaving(false);
      }
    },
    [accessToken],
  );

  const remove = useCallback(
    async (employeeCertificationId: number) => {
      await deleteEmployeeCertification(
        employeeCertificationId,
        accessToken,
      );
    },
    [accessToken],
  );

  return { data, categories, loading, saving, error, save, remove };
}
